import React from "react";
import { motion } from "motion/react";
import { Briefcase, GraduationCap, Calendar } from "lucide-react";

const experiences = [
  {
    id: "exp-1",
    title: "Freelance Full-Stack Developer",
    company: "Independent Projects",
    date: "Jan 2026 — PRESENT",
    description: "Delivered custom web and application solutions including business websites, booking platforms, and admin dashboards. Managed the full development lifecycle from UI design/prototyping to deployment.",
  },
  {
    id: "exp-2",
    title: "Application & Platform Developer",
    company: "Independent Development",
    date: "2026 — PRESENT",
    description: "Developed internal tools and web platforms for staff management and workflow automation. Implemented APIs, database integrations, and automated repetitive steps for routine tasks.",
  },
  {
    id: "exp-3",
    title: "Web Developer — Business Solutions",
    company: "Independent Projects",
    date: "2025 — 2025",
    description: "Built production websites for service and commercial businesses (cleaning services, restaurants, automotive). Implemented reservation systems, galleries, and admin panels.",
  },
  {
    id: "exp-4",
    title: "Freelancer (Frontend Web Developer)",
    company: "Fiverr & Upwork",
    date: "Jan 2020 — PRESENT",
    description: "Delivered client websites and landing pages with modern UI patterns. Implemented contact/booking forms and handled deployment tasks including hosting, DNS, and SSL.",
  },
  {
    id: "exp-5",
    title: "Frontend Developer Assistant",
    company: "Digital School",
    date: "Feb 2022 — Mar 2023",
    description: "Built responsive UI pages and reusable components using HTML, CSS, and JS. Supported React-based projects and improved accessibility and consistency.",
  },
];

const education = [
  {
    id: "edu-1",
    degree: "Bachelor (In Progress)",
    school: "University \"Ukshin Hoti\", Prizren",
    date: "Oct 2023 — PRESENT",
  },
  {
    id: "edu-2",
    degree: "Computer Science Diploma",
    school: "Middle School for Computer Science",
    date: "2020 — 2022",
  },
  {
    id: "edu-3",
    degree: "Web Development Course",
    school: "Digital School",
    date: "2018 — 2022",
  },
];

export function Experience() {
  return (
    <div className="grid md:grid-cols-3 gap-16">
      <div className="md:col-span-2 space-y-12">
        <h3 className="text-2xl font-black uppercase tracking-tighter mb-12 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-violet-600/10 border border-violet-500/20 flex items-center justify-center text-violet-400 shadow-[0_0_20px_rgba(139,92,246,0.2)]">
            <Briefcase size={22} />
          </div>
          <span className="text-white italic">Work History</span>
        </h3>
        <div className="space-y-16 relative ml-6 pl-12">
          {/* Glowing Timeline Line */}
          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-violet-500 via-fuchsia-500 to-transparent opacity-30" />
          
          {experiences.map((exp, index) => (
            <motion.div
              key={exp.id}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
              className="relative group"
            >
              {/* Glowing Connector Dot */}
              <div className="absolute -left-[58px] top-0 w-10 h-10 bg-neutral-950 border-2 border-neutral-800 rounded-2xl group-hover:border-violet-500 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] transition-all duration-500 flex items-center justify-center z-10">
                <div className="w-2 h-2 rounded-full bg-violet-500 group-hover:scale-150 group-hover:bg-white transition-all" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h4 className="text-2xl font-black uppercase tracking-tight text-white group-hover:text-violet-400 transition-colors">{exp.title}</h4>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-400 bg-violet-500/10 px-4 py-2 rounded-xl border border-violet-500/20 flex items-center gap-2 self-start sm:self-center shadow-[0_0_15px_rgba(139,92,246,0.1)]">
                   <Calendar size={14} className="text-violet-500" /> {exp.date}
                </span>
              </div>
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400 font-black mb-6 uppercase text-[10px] tracking-[0.3em]">{exp.company}</p>
              <p className="text-neutral-500 text-base md:text-lg leading-relaxed max-w-2xl font-medium group-hover:text-neutral-300 transition-colors">{exp.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="space-y-12">
        <h3 className="text-2xl font-black uppercase tracking-tighter mb-12 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-cyan-600/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
            <GraduationCap size={22} />
          </div>
          <span className="text-white italic">Education</span>
        </h3>
        <div className="space-y-8">
          {education.map((edu, index) => (
            <motion.div
              key={edu.id}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
              className="p-8 bg-neutral-900/40 backdrop-blur-md border border-neutral-800/50 rounded-3xl hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)] transition-all duration-500 group"
            >
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-500/70 block mb-4 group-hover:text-cyan-400 transition-colors">{edu.date}</span>
              <h4 className="font-black text-xl uppercase tracking-tight mb-2 text-white">{edu.degree}</h4>
              <p className="text-sm text-neutral-500 font-bold uppercase tracking-widest">{edu.school}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}